import { useState } from 'react';
import { api, errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function DocumentCategorySuggestions({ onReload }) {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/documents/category-preview');
      setPreview(data);
      setSelected(new Set(data.documents.filter((d) => d.category).map((d) => d.id)));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const toggle = (ids, enabled) => setSelected((old) => {
    const next = new Set(old);
    ids.forEach((id) => enabled ? next.add(id) : next.delete(id));
    return next;
  });
  const apply = async () => {
    setBusy(true);
    try {
      let modified = 0, skipped = 0;
      const ids = [...selected];
      // The endpoint caps a selection at 2,000 documents.
      for (let i = 0; i < ids.length; i += 2000) {
        const { data } = await api.post('/documents/category-apply', { document_ids: ids.slice(i, i + 2000), categories: Object.fromEntries(preview.documents.filter((d) => selected.has(d.id)).map((d) => [d.id, d.category])) });
        modified += data.modified; skipped += data.skipped;
      }
      toast.success(`${modified} document(s) classé(s)${skipped ? ` · ${skipped} ignoré(s), situation modifiée depuis l’aperçu` : ''}`);
      setPreview(null);
      await onReload();
    } catch (e) {
      toast.error(errMsg(e));
      setPreview(null);
      await onReload();
    } finally { setBusy(false); }
  };
  const groups = new Map();
  for (const d of preview?.documents || []) {
    const label = d.category || 'À vérifier — non classés';
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(d);
  }
  return <>
    <Button variant="outline" disabled={busy} onClick={load}>Classer les documents existants</Button>
    {preview && <Dialog open onOpenChange={(open) => !open && !busy && setPreview(null)}><DialogContent className="max-w-3xl">
      <DialogHeader><DialogTitle>Vérifier les propositions de classement</DialogTitle></DialogHeader>
      <p className="text-sm">{preview.preserved} document(s) déjà classé(s) conservé(s). Propositions fondées sur les URL et les sources, sans IA ni réindexation. Décochez les propositions à écarter.</p>
      <div className="max-h-[55vh] overflow-y-auto space-y-3">{[...groups].map(([label, docs]) => <details key={label} className="border rounded p-3">
        <summary className="cursor-pointer"><span className="inline-flex items-center gap-2">
          {docs[0].category && <input type="checkbox" disabled={busy} aria-label={`Valider la catégorie ${label}`}
            checked={docs.every((d) => selected.has(d.id))}
            ref={(el) => { if (el) el.indeterminate = docs.some((d) => selected.has(d.id)) && !docs.every((d) => selected.has(d.id)); }}
            onClick={(e) => e.stopPropagation()} onChange={(e) => toggle(docs.map((d) => d.id), e.target.checked)} />}
          {label} · {docs.length} document(s)</span></summary>
        <div className="mt-2 space-y-2">{docs.map((d) => <label key={d.id} className="flex gap-2 text-sm">
          {d.category && <input type="checkbox" disabled={busy} checked={selected.has(d.id)} onChange={(e) => toggle([d.id], e.target.checked)} />}
          <span className="min-w-0 break-words">{d.title}<span className="block text-xs text-muted-foreground">{d.source_url}<br />{d.reason}</span></span>
        </label>)}</div>
      </details>)}{!groups.size && <p>Aucun document à classer.</p>}</div>
      <div className="flex gap-2"><Button disabled={busy || !selected.size} onClick={apply}>{busy ? 'Classement…' : `Appliquer aux ${selected.size} documents sélectionnés`}</Button>
        <Button variant="outline" disabled={busy} onClick={() => setPreview(null)}>Annuler</Button></div>
    </DialogContent></Dialog>}
  </>;
}
